import {
  DownloadSimpleIcon,
  FileArchiveIcon,
  SpinnerGapIcon,
  UploadSimpleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { useRef, useState } from 'react';

import { ICON_WEIGHT } from '@/components/icon';
import { Button } from '@/components/ui/button';
import { formatLocalDate, formatLocalTime } from '@/lib/format';
import { saveFile, shareOrSaveFile } from '@/lib/track-export';

import { buildBackupArchive, buildTrackFilesArchive, restoreBackupArchive } from './archive';
import { summariseMerge } from './summary';
import { BACKUP_HASH, recordBackupTaken, useLastBackupAt } from './reminder';

/**
 * Export and restore the `.fdv` archive — `PLAN.md` §6 Phase 4. Lives on the
 * Settings page, the same "one panel, no modal" shape as the export composer
 * and settings' other sections.
 */

export function BackupPanel() {
  const lastBackupAt = useLastBackupAt();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'busy'>('idle');
  const [result, setResult] = useState<{ kind: 'ok' | 'error'; message: string } | null>(null);

  const handleExport = async () => {
    setStatus('busy');
    setResult(null);
    try {
      const blob = await buildBackupArchive();
      const outcome = await shareOrSaveFile(
        `flight-data-viewer-${new Date().toISOString().slice(0, 10)}.fdv`,
        blob,
        'application/zip',
      );
      if (outcome === 'cancelled') {
        return;
      }
      await recordBackupTaken();
      setResult({ kind: 'ok', message: 'Backup saved.' });
    } catch (error) {
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'The backup could not be created.',
      });
    } finally {
      setStatus('idle');
    }
  };

  const handleTrackFiles = async () => {
    setStatus('busy');
    setResult(null);
    try {
      const blob = await buildTrackFilesArchive();
      if (blob === null) {
        setResult({ kind: 'ok', message: 'No tracks stored.' });
        return;
      }
      saveFile(
        `flight-data-viewer-tracks-${new Date().toISOString().slice(0, 10)}.zip`,
        blob,
        'application/zip',
      );
    } catch (error) {
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'Track files could not be exported.',
      });
    } finally {
      setStatus('idle');
    }
  };

  const handleFile = async (file: File) => {
    setStatus('busy');
    setResult(null);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const summary = await restoreBackupArchive(bytes);
      setResult({ kind: 'ok', message: summariseMerge(summary) });
    } catch (error) {
      setResult({
        kind: 'error',
        message: error instanceof Error ? error.message : 'This backup could not be restored.',
      });
    } finally {
      setStatus('idle');
    }
  };

  return (
    /*
     * `id="backup"` is the target of every "Back up" link in the app
     * (`BACKUP_LINK`), so they land on this panel rather than the top of a
     * long Settings page. `scroll-mt` keeps its heading off the window edge.
     */
    <section
      id={BACKUP_HASH}
      className="border-border bg-surface rounded-container scroll-mt-4 border p-4"
    >
      <h2 className="text-sm font-semibold tracking-tight">Backup</h2>
      <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
        One file with all flights, track files, aircraft and settings. Browser storage can be
        cleared without warning. Store a copy elsewhere.
      </p>
      <p className="text-muted-foreground mt-2 text-xs" data-testid="last-backup-status">
        {lastBackupAt === undefined
          ? ''
          : lastBackupAt === null
            ? 'No backup taken yet.'
            : `Last backed up ${formatLocalDate(lastBackupAt)}, ${formatLocalTime(lastBackupAt)}.`}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          disabled={status === 'busy'}
          onClick={() => {
            void handleExport();
          }}
        >
          {status === 'busy' ? (
            <SpinnerGapIcon
              aria-hidden="true"
              weight={ICON_WEIGHT}
              className="size-4 motion-safe:animate-spin"
            />
          ) : (
            <DownloadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          )}
          Back up now
        </Button>
        <Button
          variant="outline"
          disabled={status === 'busy'}
          onClick={() => {
            fileInputRef.current?.click();
          }}
        >
          <UploadSimpleIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Restore from a backup
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".fdv"
          className="sr-only"
          aria-label="Choose a backup file to restore"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file !== undefined) {
              void handleFile(file);
            }
          }}
        />
      </div>

      {/*
        Apart from backup and restore: this is an export of the original
        files for use elsewhere, not a way to get the app's data back.
      */}
      <div className="border-border mt-4 flex flex-wrap items-center justify-between gap-3 border-t pt-4">
        <div>
          <h3 className="text-sm font-medium">Track files</h3>
          <p className="text-muted-foreground text-xs">
            Original files as imported, in one zip. GPX where no original is stored.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={status === 'busy'}
          onClick={() => {
            void handleTrackFiles();
          }}
        >
          <FileArchiveIcon aria-hidden="true" weight={ICON_WEIGHT} className="size-4" />
          Download track files
        </Button>
      </div>

      {result === null ? null : (
        <p
          role={result.kind === 'error' ? 'alert' : undefined}
          className={`mt-2 flex items-center gap-1.5 text-xs ${
            result.kind === 'error' ? 'text-danger' : 'text-muted-foreground'
          }`}
        >
          {result.kind === 'error' ? (
            <WarningCircleIcon
              aria-hidden="true"
              weight={ICON_WEIGHT}
              className="size-3.5 shrink-0"
            />
          ) : null}
          {result.message}
        </p>
      )}
    </section>
  );
}
